import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { BrandOverallRating } from "@faire/retailer-visitor-shared/components/RatingStars/BrandOverallRating";
import { getBrandLocationString } from "@faire/retailer-visitor-shared/consts/getBrandLocationString";
import { Tag } from "@faire/retailer-visitor-shared/views/brand/components/Tag";
import { BrandMinimumInfo } from "@faire/retailer-visitor-shared/views/faire-direct/__internal__/BrandMinimumInfo";
import { ProductPreviewCarousel } from "@faire/retailer-visitor-shared/views/faire-direct/components/ProductPreviewCarousel";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { Avatar } from "@faire/web--source/slate/Avatar";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";
var BrandPreviewBase = function(param) {
    var item = param.item, brand = param.brand, totalProductCount = param.totalProductCount, productTiles = param.productTiles, loadTimingRef = param.loadTimingRef;
    var _item_brand_reviews_summary;
    var isTabletAndAbove = useViewport().isTabletAndAbove;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var basedIn = getBrandLocationString({
        city: item.brand_based_in_city,
        state: item.brand_based_in_state,
        addBasedIn: true
    }, strictLocalize);
    var _item_brand_reviews_summary_average_rating;
    var averageRating = (_item_brand_reviews_summary_average_rating = (_item_brand_reviews_summary = item.brand_reviews_summary) === null || _item_brand_reviews_summary === void 0 ? void 0 : _item_brand_reviews_summary.average_rating) !== null && _item_brand_reviews_summary_average_rating !== void 0 ? _item_brand_reviews_summary_average_rating : 0;
    var extraProductCount = totalProductCount ? totalProductCount - productTiles.length : 0;
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsxs(PictureContainer, {
                children: [
                    /*#__PURE__*/ _jsx(PictureOverlay, {}),
                    /*#__PURE__*/ _jsx(Avatar, {
                        image: item.brand_profile_image,
                        size: isTabletAndAbove ? "x-large" : "large"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(BrandInfoArea, {
                children: [
                    /*#__PURE__*/ _jsx(LabelContainer, {
                        variant: "displaySSerifRegular",
                        "data-test-id": "productGridHeader",
                        children: item.brand_name
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsxs(Typography, {
                        color: Core.text.subdued,
                        children: [
                            basedIn ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    basedIn,
                                    /*#__PURE__*/ _jsx("br", {})
                                ]
                            }) : null,
                            /*#__PURE__*/ _jsx(BrandMinimumInfo, {
                                brand_first_order_minimum: item.brand_first_order_minimum,
                                brand_reorder_minimum: item.brand_reorder_minimum
                            })
                        ]
                    }),
                    averageRating >= 4 ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Spacer, {
                                height: "1x"
                            }),
                            /*#__PURE__*/ _jsx(BrandOverallRating, {
                                rating: averageRating
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        style: {
                            margin: "0 0 -8px 0",
                            flexWrap: "wrap"
                        },
                        children: brand.tags.map(function(param) {
                            var tag = param.tag, token = param.token;
                            return /*#__PURE__*/ _jsx(Tag, {
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    children: tag
                                })
                            }, token);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ProductsTitle, {
                variant: "displaySSerifRegular",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "jczseN",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Featured Products"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ProductPreviewCarouselWrapper, {
                ref: loadTimingRef,
                children: /*#__PURE__*/ _jsx(ProductPreviewCarousel, {
                    brandToken: item.brand_token,
                    productTiles: productTiles,
                    moreProductCount: extraProductCount
                })
            })
        ]
    });
};
export var BrandPreview = observer(BrandPreviewBase);
var Wrapper = styled.li.withConfig({
    componentId: "sc-a8a9a2ef-0"
})([
    'display:grid;grid-column-gap:16px;position:relative;grid-template:"profile-picture  brand-info      " "profile-picture  .               " 16px "profile-picture  .               " "products-title   products-title  " 24px ".                .               " 24px "product-list     product-list    " / 64px 1fr;',
    '{grid-template:"profile-picture  brand-info      ." ".                .               ." 24px "products-title   products-title  ." 24px ".                .               ." 24px "product-list     product-list    product-list" / 96px 1fr auto;padding:64px 0 0 0;&:first-child{padding:0 0 64px;}}',
    '{align-items:start;grid-template:"profile-picture  products-title" "profile-picture  product-list" ".                product-list" 16px "brand-info       product-list" / 256px;padding:40px 0 0 0;}'
], responsiveMediaQueries.tabletAndAbove, responsiveMediaQueries.desktopAndAbove);
var BrandInfoArea = styled.div.withConfig({
    componentId: "sc-a8a9a2ef-1"
})([
    "grid-area:brand-info;align-self:center;overflow:hidden;",
    "{align-self:start;}"
], responsiveMediaQueries.desktopAndAbove);
var LabelContainer = styled(Typography).withConfig({
    componentId: "sc-a8a9a2ef-2"
})([
    "max-width:241px;",
    "{max-width:435px;}",
    "{max-width:256px;}"
], responsiveMediaQueries.tabletAndAbove, responsiveMediaQueries.desktopAndAbove);
var PictureContainer = styled.div.withConfig({
    componentId: "sc-a8a9a2ef-3"
})([
    "grid-area:profile-picture;width:fit-content;"
]);
var ProductsTitle = styled(Typography).withConfig({
    componentId: "sc-a8a9a2ef-4"
})([
    "&&&{grid-area:products-title;margin-bottom:15px;}"
]);
var ProductPreviewCarouselWrapper = styled.div.withConfig({
    componentId: "sc-a8a9a2ef-5"
})([
    "grid-area:product-list;"
]);
var PictureOverlay = styled.span.withConfig({
    componentId: "sc-a8a9a2ef-6"
})([
    "position:absolute;width:64px;height:64px;border-radius:100px;background:rgba(0,0,0,0.05);",
    "{width:96px;height:96px;}"
], responsiveMediaQueries.tabletAndAbove);
